import type { ChangeSubtask, ChangeWorkGroup } from '../../types/changes';

export const ASSIGNMENT_SETOR = 'setor';
export const ASSIGNMENT_PESSOA = 'pessoa';

export type SubtaskAssignmentKind = typeof ASSIGNMENT_SETOR | typeof ASSIGNMENT_PESSOA;

export function membersOfSector(areas: ChangeWorkGroup[], groupId: string) {
  const area = areas.find((item) => String(item.group_id) === groupId);
  return (area?.members || []).filter((member) => member.user_id);
}

export function peopleFromAreas(areas: ChangeWorkGroup[]) {
  const byId = new Map<number, { user_id: number; name?: string | null }>();
  areas.forEach((area) => {
    (area.members || []).forEach((member) => {
      if (member.user_id && !byId.has(member.user_id)) {
        byId.set(member.user_id, member);
      }
    });
  });
  return Array.from(byId.values()).sort((left, right) =>
    (left.name || '').localeCompare(right.name || '', 'pt-BR'),
  );
}

export function subtaskResponsibleLabel(subtask: ChangeSubtask): string {
  const linkedIncident = subtask.origin === 'incidente' || Boolean(subtask.ticket_id);
  if (linkedIncident) {
    const incidentName = subtask.incident?.taken_by_name?.trim();
    if (incidentName) return incidentName;
    if (!subtask.incident?.taken_by_id) {
      return 'Aguardando no Incidente';
    }
    return subtask.responsible_name?.trim() || '—';
  }
  if (subtask.status === 'em_andamento') {
    return 'Aguardando assunção';
  }
  return subtask.responsible_name?.trim() || '—';
}

export function assignmentLabel(kind?: string | null, assigneeName?: string | null): string {
  if (kind === ASSIGNMENT_PESSOA) {
    return assigneeName?.trim() ? assigneeName : 'Pessoa específica';
  }
  return 'Qualquer membro do grupo';
}

export function canAssumeSubtask(opts: {
  changeOperable: boolean;
  status: string;
  assignmentKind?: string | null;
  assigneeId?: number | null;
  isSectorMember: boolean;
  userId?: number | null;
}): boolean {
  if (!opts.changeOperable || opts.status !== 'em_andamento') return false;
  if (opts.assignmentKind === ASSIGNMENT_PESSOA) {
    return opts.userId != null && opts.userId === opts.assigneeId;
  }
  return opts.isSectorMember;
}

export function canTalkOnSubtask(opts: {
  changeFrozen: boolean;
  hasTicket: boolean;
  assignmentKind?: string | null;
  assigneeId?: number | null;
  createdBy?: number | null;
  isSectorMember: boolean;
  isStakeholder: boolean;
  userId?: number | null;
}): boolean {
  if (opts.changeFrozen || !opts.hasTicket) return false;
  return canAttachOnSubtask({ ...opts, changeFrozen: opts.changeFrozen });
}

export function canAnswerChecklist(opts: {
  changeOperable: boolean;
  status: string;
  assignmentKind?: string | null;
  assigneeId?: number | null;
  responsibleId?: number | null;
  ticketId?: number | null;
  isSectorMember: boolean;
  userId?: number | null;
}): boolean {
  if (!opts.changeOperable || opts.status === 'concluida' || opts.status === 'em_andamento') {
    return false;
  }
  if (opts.assignmentKind === ASSIGNMENT_PESSOA) {
    return opts.userId != null && opts.userId === opts.assigneeId;
  }
  return Boolean(opts.isSectorMember && opts.userId != null && opts.userId === opts.responsibleId);
}

export function canAttachOnSubtask(opts: {
  changeFrozen: boolean;
  assignmentKind?: string | null;
  assigneeId?: number | null;
  createdBy?: number | null;
  isSectorMember: boolean;
  isStakeholder: boolean;
  userId?: number | null;
}): boolean {
  if (opts.changeFrozen) return false;
  if (opts.isStakeholder) return true;
  if (opts.userId != null && opts.createdBy === opts.userId) return true;
  if (opts.assignmentKind === ASSIGNMENT_PESSOA) {
    return opts.userId != null && opts.userId === opts.assigneeId;
  }
  return opts.isSectorMember;
}
